import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ApiService } from '../api/api.service';
import { GlobalService } from '../api/global.service';

export interface TimeEntryFilters {
  user_guid?: string;
  project_guid?: string;
  task_guid?: string;
  step_guid?: string;
  client_guid?: string;
  company_guid?: string;
  start?: string;
  end?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TimeAndBillingService {

  // Fires whenever a time entry is created or deleted, so every visible list of time entries can reload itself
  private changed = new Subject<void>();
  readonly changes$ = this.changed.asObservable();

  NotifyChanged() {
    this.changed.next();
  }

  constructor(private apiService: ApiService, private globalService: GlobalService) { }

  // Returns the entries (already shaped for the UI), or [] when the call fails
  async GetEntries(filters: TimeEntryFilters = {}): Promise<any[]> {
    const query = Object.keys(filters)
      .filter(key => !!filters[key])
      .map(key => `${key}=${encodeURIComponent(filters[key])}`)
      .join('&');
    const data: any = await this.apiService.get(query ? `time_and_billing?${query}` : 'time_and_billing');
    if (!data?.success) return [];
    return (data.data || []).map(entry => this.MapEntry(entry));
  }

  async Create(entry: any): Promise<any> {
    const data: any = await this.apiService.post('time_and_billing', entry);
    if (data?.success && Array.isArray(data.data) && data.data.length) data.data = this.MapEntry(data.data[0]);
    return data;
  }

  // Returns the saved entry (already shaped for the UI) in data when it worked
  async Update(guid: string, entry: any): Promise<any> {
    const data: any = await this.apiService.put(`time_and_billing/${guid}`, entry);
    if (data?.success && Array.isArray(data.data) && data.data.length) data.data = this.MapEntry(data.data[0]);
    return data;
  }

  async Delete(guid: string): Promise<any> {
    return await this.apiService.delete(`time_and_billing/${guid}`, null);
  }

  async SetTimeStatus(guids: string[], time_status: string): Promise<any> {
    return await this.apiService.patch('time_and_billing/status', { guids, time_status });
  }

  // The API returns flat rows; the UI works with nested project / client / company objects
  // What the entry is linked to, for the table's Linked column. An entry is linked to one thing: an appointment,
  // to do, missing info, or a project (shown as its task when it has one). text is what the column shows / filters on.
  private GetLinked(entry: any): { type: string, label: string, guid: string, title: string, text: string } | null {
    const make = (type: string, label: string, guid: string, title: string) => ({ type, label, guid, title: title || '', text: `${label}: ${title || ''}` });
    if (entry.appointment_guid) return make('appointment', 'Appointment', entry.appointment_guid, entry.appointment_title);
    if (entry.todo_guid) return make('todo', 'To Do', entry.todo_guid, entry.todo_title);
    if (entry.missing_info_guid) return make('missing_info', 'Missing Info', entry.missing_info_guid, entry.missing_info_title);
    if (entry.task_guid) return make('task', 'Task', entry.task_guid, entry.task_title);
    if (entry.project_guid) return make('project', 'Project', entry.project_guid, entry.project_title);
    return null;
  }

  private MapEntry(entry: any): any {
    // The API returns the stored UTC datetimes as they are (no zone) - mark them as UTC like the rest of the app does
    entry.start_date = this.globalService.DateToUTCFormat(entry.start_date);
    entry.end_date = this.globalService.DateToUTCFormat(entry.end_date);
    entry.created_date = this.globalService.DateToUTCFormat(entry.created_date);
    if (entry.rounded_start_date) entry.rounded_start_date = this.globalService.DateToUTCFormat(entry.rounded_start_date);
    if (entry.rounded_end_date) entry.rounded_end_date = this.globalService.DateToUTCFormat(entry.rounded_end_date);
    entry.project_groups = entry.project_groups || [];
    // A task's entry shows the project the task belongs to too, even when the entry itself wasn't saved with the project
    if (!entry.project_guid && entry.task_guid && entry.task_project_guid) {
      entry.project_guid = entry.task_project_guid;
      entry.project_title = entry.task_project_title;
    }
    entry.project = entry.project_guid ? { guid: entry.project_guid, title: entry.project_title } : null;
    entry.client = entry.client_guid ? { guid: entry.client_guid, display_name: entry.client_display_name } : null;
    entry.company = entry.company_guid
      ? { guid: entry.company_guid, name: entry.company_name, display_name: entry.company_name }
      : null;
    entry.tags = entry.tags || [];
    entry.linked = this.GetLinked(entry);
    return entry;
  }
}
